import { 
  PreEntry, 
  UploadRequest, 
  UploadResponse, 
  ParseRequest, 
  ParseResponse, 
  ProcessRequest, 
  ProcessResponse,
  ListPreEntriesResponse,
  Section,
  Category,
  Template,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  ListTemplatesResponse
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.yourdomain.com';

class ApiService {
  private async request<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Get auth token from localStorage
    const token = localStorage.getItem('accessToken');
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        // Try both formats - first without Bearer, then with Bearer if needed
        ...(token && { Authorization: token }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.error || errorMessage;
      } catch {
        // Use the default error message if JSON parsing fails
      }
      
      throw new Error(errorMessage);
    }

    return response.json();
  }

  // File upload methods
  async getUploadUrl(request: UploadRequest): Promise<UploadResponse> {
    return this.request<UploadResponse>('/upload', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async uploadFile(uploadUrl: string, file: File): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }
  }

  async parseFile(request: ParseRequest): Promise<ParseResponse> {
    return this.request<ParseResponse>('/parse', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Pre-entries methods
  async listPreEntries(params?: {
    status?: 'pending' | 'saved' | 'processed';
    limit?: number;
    lastKey?: string;
  }): Promise<ListPreEntriesResponse> {
    const searchParams = new URLSearchParams();
    
    if (params?.status) searchParams.append('status', params.status);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.lastKey) searchParams.append('last_key', params.lastKey);

    const query = searchParams.toString();
    const endpoint = `/pre-entries${query ? `?${query}` : ''}`;
    
    return this.request<ListPreEntriesResponse>(endpoint);
  }

  async getPreEntry(id: string): Promise<PreEntry> {
    return this.request<PreEntry>(`/pre-entries/${id}`);
  }

  async createPreEntry(data: {
    date: string;
    amount: number;
    description: string;
    category?: string;
    section?: string;
  }): Promise<PreEntry> {
    return this.request<PreEntry>('/pre-entries', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePreEntry(
    id: string, 
    updates: {
      category?: string;
      section?: string;
      selected_section_id?: string;
      selected_section_name?: string;
      selected_category_id?: string;
      selected_category_name?: string;
      description?: string;
      amount?: number;
    }
  ): Promise<PreEntry> {
    return this.request<PreEntry>(`/pre-entries/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deletePreEntry(id: string): Promise<void> {
    const url = `${API_BASE_URL}/pre-entries/${id}`;
    
    // Get auth token from localStorage
    const token = localStorage.getItem('accessToken');
    
    const config: RequestInit = {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: token }),
      },
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.error || errorMessage;
      } catch {
        // Use the default error message if JSON parsing fails
      }
      
      throw new Error(errorMessage);
    }

    // For DELETE operations, we don't try to parse JSON since 204 returns no content
  }

  // Processing methods
  async processPreEntries(request: ProcessRequest): Promise<ProcessResponse> {
    return this.request<ProcessResponse>('/process', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Utility methods
  async listAvailableParsers(): Promise<string[]> {
    const response = await this.request<{ parsers: string[] }>('/parsers');
    return response.parsers;
  }

  // Sections and Categories methods
  async listSections(): Promise<Section[]> {
    return this.request<Section[]>('/sections');
  }

  async listCategories(sectionName: string): Promise<{ categories: Category[] }> {
    const params = new URLSearchParams({ section: sectionName });
    return this.request<{ categories: Category[] }>(`/categories?${params.toString()}`);
  }

  // Template methods
  async listTemplates(): Promise<ListTemplatesResponse> {
    return this.request<ListTemplatesResponse>('/templates');
  }

  async getTemplate(id: string): Promise<Template> {
    return this.request<Template>(`/templates/${id}`);
  }

  async createTemplate(data: CreateTemplateRequest): Promise<Template> {
    return this.request<Template>('/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTemplate(id: string, updates: UpdateTemplateRequest): Promise<Template> {
    return this.request<Template>(`/templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteTemplate(id: string): Promise<void> {
    const url = `${API_BASE_URL}/templates/${id}`;
    
    // Get auth token from localStorage
    const token = localStorage.getItem('accessToken');
    
    const config: RequestInit = {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: token }),
      },
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.error || errorMessage;
      } catch {
        // Use the default error message if JSON parsing fails
      }
      
      throw new Error(errorMessage);
    }

    // For DELETE operations, we don't try to parse JSON since 204 returns no content
  }

  // File upload with progress tracking
  async uploadFileWithProgress(
    file: File,
    bankType?: string,
    onProgress?: (progress: number) => void
  ): Promise<ParseResponse> {
    // Step 1: Get upload URL
    onProgress?.(10);
    const uploadRequest: UploadRequest = {
      file_name: file.name,
      content_type: file.type,
      file_size: file.size,
    };
    
    const { upload_url, file_key } = await this.getUploadUrl(uploadRequest);
    
    // Step 2: Upload file to S3
    onProgress?.(30);
    await this.uploadFile(upload_url, file);
    
    // Step 3: Parse the file
    onProgress?.(70);
    const parseRequest: ParseRequest = {
      file_key,
      ...(bankType && { bank_type: bankType }),
    };
    
    const result = await this.parseFile(parseRequest);
    onProgress?.(100);
    
    return result;
  }
}

export const apiService = new ApiService();